import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const toMonthStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function NightShiftSchedule() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const today = new Date();
  const todayStr = toDateStr(today);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const month = selectedDate.slice(0, 7); // YYYY-MM for API
  const [expandedDate, setExpandedDate] = useState(null);
  const [expandedDept, setExpandedDept] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignAssignment, setReassignAssignment] = useState(null);
  const [reassignNewUserId, setReassignNewUserId] = useState("");
  const [reassignSaving, setReassignSaving] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapAssignmentA, setSwapAssignmentA] = useState(null);
  const [swapAssignmentB, setSwapAssignmentB] = useState(null);
  const [swapSaving, setSwapSaving] = useState(false);

  const { data: assignmentsData, isLoading, isError, error, refetch } = useFetchObjects(
    ["night-shift-assignments", month],
    `night-shift-assignments/?month=${month}&page_size=500`,
    token
  );
  const assignments = useMemo(() => {
    const raw = assignmentsData?.results ?? assignmentsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [assignmentsData]);

  const byDateThenDept = useMemo(() => {
    const map = {};
    assignments.forEach((a) => {
      const d = a.date;
      if (!map[d]) map[d] = {};
      const dept = a.department_display || t("nightShift.noDepartment", "No department");
      if (!map[d][dept]) map[d][dept] = [];
      map[d][dept].push(a);
    });
    const dates = Object.keys(map).sort();
    return { map, dates };
  }, [assignments, t]);

  // Show only the selected date (single day view)
  const displayDates = useMemo(
    () => (byDateThenDept.dates.includes(selectedDate) ? [selectedDate] : []),
    [byDateThenDept.dates, selectedDate]
  );
  const hasDataForSelectedDate = byDateThenDept.dates.includes(selectedDate);

  // Auto-expand the single date when showing one day
  useEffect(() => {
    if (displayDates.length === 1) setExpandedDate(displayDates[0]);
    else setExpandedDate(null);
  }, [displayDates.join(",")]);

  const handleToggleDate = (date) => {
    setExpandedDate((prev) => (prev === date ? null : date));
    setExpandedDept(null);
  };
  const handleToggleDept = (date, dept) => {
    const key = `${date}-${dept}`;
    setExpandedDept((prev) => (prev === key ? null : key));
  };

  const baseUrl = process.env.REACT_APP_API_URL || "";

  const handleReassignOpen = (assignment) => {
    setReassignAssignment(assignment);
    setReassignNewUserId("");
    setReassignOpen(true);
  };
  const handleReassignClose = () => {
    setReassignOpen(false);
    setReassignAssignment(null);
    setReassignNewUserId("");
  };
  const handleReassignConfirm = async () => {
    if (!reassignAssignment || !reassignNewUserId) return;
    setReassignSaving(true);
    try {
      await axios.delete(
        `${baseUrl}/night-shift-assignments/${reassignAssignment.id}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      await axios.post(
        `${baseUrl}/night-shift-assignments/`,
        { user: Number(reassignNewUserId), date: reassignAssignment.date },
        { headers: { Authorization: `Token ${token}` } }
      );
      toast.success(t("nightShift.reassigned", "Reassigned."));
      refetch();
      handleReassignClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || t("nightShift.reassignError", "Failed to reassign."));
    } finally {
      setReassignSaving(false);
    }
  };

  const handleSwapOpen = (assignmentA) => {
    setSwapAssignmentA(assignmentA);
    setSwapAssignmentB(null);
    setSwapOpen(true);
  };
  const handleSwapClose = () => {
    setSwapOpen(false);
    setSwapAssignmentA(null);
    setSwapAssignmentB(null);
  };
  const otherAssignments = useMemo(
    () => assignments.filter((a) => a.id !== swapAssignmentA?.id),
    [assignments, swapAssignmentA]
  );
  const handleSwapConfirm = async () => {
    if (!swapAssignmentA || !swapAssignmentB) return;
    setSwapSaving(true);
    try {
      await axios.post(
        `${baseUrl}/night-shift-assignments/swap/`,
        {
          date_a: swapAssignmentA.date,
          user_a: swapAssignmentA.user,
          date_b: swapAssignmentB.date,
          user_b: swapAssignmentB.user,
        },
        { headers: { Authorization: `Token ${token}` } }
      );
      toast.success(t("nightShift.swapped", "Swap completed."));
      refetch();
      handleSwapClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || t("nightShift.swapError", "Failed to swap."));
    } finally {
      setSwapSaving(false);
    }
  };

  const { data: usersData } = useFetchObjects(
    ["users-list-night-shift"],
    "users/?page_size=500",
    token,
    { enabled: reassignOpen }
  );
  const users = useMemo(() => {
    const raw = usersData?.results ?? usersData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [usersData]);

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error?.message || t("nightShift.loadError", "Failed to load schedule.")}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("nightShift.scheduleTitle", "Night shift schedule")}
        </Typography>
        <TextField
          type="date"
          label={t("nightShift.date", "Date")}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: "9999-12-31" }}
        />
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !hasDataForSelectedDate ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {t("nightShift.noAssignmentsForDate", "No night shift assignments for this date.")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {displayDates.map((date) => {
              const depts = byDateThenDept.map[date];
              const deptNames = Object.keys(depts);
              const isDateExpanded = expandedDate === date;
              return (
                <Card key={date}>
                  <CardContent sx={{ py: 1 }}>
                    <ListItem
                      button
                      onClick={() => handleToggleDate(date)}
                      sx={{ px: 0 }}
                    >
                      <ListItemText
                        primary={date}
                        secondary={`${deptNames.length} ${t("nightShift.departments", "department(s)")}`}
                      />
                      {isDateExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItem>
                    <Collapse in={isDateExpanded} timeout="auto" unmountOnExit>
                      <List dense disablePadding sx={{ pl: 2 }}>
                        {deptNames.map((dept) => {
                          const list = depts[dept];
                          const key = `${date}-${dept}`;
                          const isDeptExpanded = expandedDept === key;
                          return (
                            <React.Fragment key={key}>
                              <ListItem
                                button
                                onClick={() => handleToggleDept(date, dept)}
                                sx={{ py: 0.5 }}
                              >
                                <ListItemText primary={`${dept} (${list.length})`} />
                                {isDeptExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </ListItem>
                              <Collapse in={isDeptExpanded} timeout="auto" unmountOnExit>
                                <List dense disablePadding sx={{ pl: 3 }}>
                                  {list.map((a) => (
                                    <ListItem key={a.id} sx={{ py: 0 }}>
                                      <ListItemText primary={a.user_display || a.user} />
                                      <ListItemSecondaryAction>
                                        <IconButton size="small" title={t("nightShift.reassign", "Reassign")} onClick={() => handleReassignOpen(a)}>
                                          <PersonAddIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" title={t("nightShift.swap", "Swap")} onClick={() => handleSwapOpen(a)}>
                                          <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      <Dialog open={reassignOpen} onClose={handleReassignClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("nightShift.reassign", "Reassign")}</DialogTitle>
        <DialogContent>
          {reassignAssignment && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("nightShift.reassignHint", "Replace")}: {reassignAssignment.user_display} {t("nightShift.onDate", "on")} {reassignAssignment.date} {t("nightShift.with", "with")}:
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                label={t("nightShift.newPerson", "New person")}
                value={reassignNewUserId}
                onChange={(e) => setReassignNewUserId(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {[u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username} {u.display_department ? `(${u.display_department})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReassignClose}>{t("common.cancel", "Cancel")}</Button>
          <Button variant="contained" onClick={handleReassignConfirm} disabled={reassignSaving || !reassignNewUserId}>
            {reassignSaving ? <CircularProgress size={24} /> : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={swapOpen} onClose={handleSwapClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("nightShift.swap", "Swap")}</DialogTitle>
        <DialogContent>
          {swapAssignmentA && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("nightShift.swapHint", "Exchange this assignment")}: {swapAssignmentA.user_display} — {swapAssignmentA.date}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("nightShift.withAssignment", "With (select one)")}:
              </Typography>
              <List dense>
                {otherAssignments.map((b) => (
                  <ListItem
                    key={b.id}
                    button
                    selected={swapAssignmentB?.id === b.id}
                    onClick={() => setSwapAssignmentB(swapAssignmentB?.id === b.id ? null : b)}
                  >
                    <ListItemText primary={`${b.user_display} — ${b.date}`} secondary={b.department_display} />
                  </ListItem>
                ))}
                {otherAssignments.length === 0 && (
                  <ListItem><ListItemText primary={t("nightShift.noOtherAssignments", "No other assignments to swap with.")} /></ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSwapClose}>{t("common.cancel", "Cancel")}</Button>
          <Button variant="contained" onClick={handleSwapConfirm} disabled={swapSaving || !swapAssignmentB}>
            {swapSaving ? <CircularProgress size={24} /> : t("nightShift.swap", "Swap")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
